import { useQuery } from '@tanstack/react-query';
import { STATUS_FEED_URL } from './serviceStatus';

/** Public status feed shapes — mirror app/admin.v1/api/src/http/routes/status.ts. */
export type ComponentKey = 'tenant_app' | 'tenant_api' | 'admin_app' | 'admin_api' | 'database' | 'messaging';
export type ComponentState = 'operational' | 'degraded' | 'outage' | 'maintenance' | 'unknown';
export type OverallState = 'operational' | 'degraded' | 'major_outage' | 'maintenance';
export type IncidentKind = 'incident' | 'maintenance' | 'notice';
export type IncidentSeverity = 'none' | 'minor' | 'major' | 'critical';
export type IncidentStatus = 'investigating' | 'identified' | 'monitoring' | 'resolved' | 'scheduled' | 'in_progress' | 'completed';
export type ReleaseKind = 'release' | 'hotfix' | 'preview';

export interface ComponentStatus {
  key: ComponentKey;
  name: string;
  description: string;
  state: ComponentState;
  detail?: string;
  latencyMs?: number;
  version?: string;
  checkedAt: string;
}

export interface IncidentUpdate {
  id: string;
  status: IncidentStatus;
  body: string;
  createdAt: string;
}

export interface Incident {
  id: string;
  kind: IncidentKind;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  body: string | null;
  components: ComponentKey[];
  published: boolean;
  startsAt: string;
  endsAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  updates: IncidentUpdate[];
}

export interface Release {
  id: string;
  version: string;
  title: string;
  notes: string | null;
  kind: ReleaseKind;
  releasedAt: string;
  published: boolean;
}

export interface PublicStatus {
  generatedAt: string;
  overall: OverallState;
  components: ComponentStatus[];
  incidents: Incident[];
  releases: Release[];
  versions: { adminApi: string; tenantApi: string | null };
}

export const COMPONENT_LABEL: Record<ComponentKey, string> = {
  tenant_app: 'Crew web app',
  tenant_api: 'Tenant API',
  admin_app: 'Operations console',
  admin_api: 'Admin API',
  database: 'Database',
  messaging: 'Text messaging',
};
export const KIND_LABEL: Record<IncidentKind, string> = { incident: 'Incident', maintenance: 'Maintenance', notice: 'Notice' };
export const SEVERITY_LABEL: Record<IncidentSeverity, string> = { none: 'Notice', minor: 'Minor', major: 'Major', critical: 'Critical' };
export const INCIDENT_STATUS_LABEL: Record<IncidentStatus, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  completed: 'Completed',
};
export const RELEASE_KIND_LABEL: Record<ReleaseKind, string> = { release: 'Release', hotfix: 'Hotfix', preview: 'Preview' };
export const isClosedStatus = (s: IncidentStatus): boolean => s === 'resolved' || s === 'completed';

/** Public, unauthenticated feed; polls every minute while the page is open. */
export function usePublicStatus(refetchMs = 60_000) {
  return useQuery({
    queryKey: ['status', 'public'],
    queryFn: async (): Promise<PublicStatus> => {
      if (!STATUS_FEED_URL) throw new Error('This deployment has no status feed configured (VITE_STATUS_API_URL).');
      const res = await fetch(STATUS_FEED_URL, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!res.ok) throw new Error(`Status unavailable (HTTP ${res.status})`);
      return (await res.json()) as PublicStatus;
    },
    refetchInterval: refetchMs,
    staleTime: 15_000,
    retry: 1,
  });
}
